import { useCallback, useEffect, useState } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";

import { ShoppingBag as ShoppingBagIcon } from "@mui/icons-material";
import { IconButton, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import { OutletRoleEnum, UserOutletsSelect } from "@/features/outlets";
import { PermissionEnum } from "@/features/permissions";
import { apiOutletClient, formatDate, getErrorMessage } from "@/features/shared";
import { SearchTextField, useSnackbar } from "@/features/ui";
import { useSearchFilter, useServerPagination } from "@/hooks";

export const InvertedLabPurchaseOrders = () => {
  const { hasPermission, user } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const {
    setRowCount,
    paginationParams,
    dataGridPaginationProps,
    resetParams,
  } = useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });

  // Initialize outlet from URL params or null
  const [outlet, setOutlet] = useState(() => {
    const outletId = searchParams.get("outletId");
    if (outletId) {
      return user.outlets.find((o) => o.id === outletId) || null;
    }
    return null;
  });

  const [salesOrderLensProducts, setSalesOrderLensProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadUnorderedSalesOrderLensProducts = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiOutletClient.get(
        "retail-sales-order-lens-products/unordered",
        {
          params: {
            ...paginationParams,
            ...(keyword && { referenceNumber: keyword }),
            hasPurchaseOrder: false,
            outletId: outlet?.id,
          },
        },
      );
      setSalesOrderLensProducts(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(
        getErrorMessage(error, "Gagal memuat pesanan penjualan lensa pre-order"),
      );
    } finally {
      setLoading(false);
    }
  }, [outlet?.id, paginationParams, keyword, setRowCount, showSnackbar]);

  useEffect(() => {
    loadUnorderedSalesOrderLensProducts();
  }, [loadUnorderedSalesOrderLensProducts]);

  const handleOutletChange = (newOutlet) => {
    setOutlet(newOutlet);

    // Update URL params
    const newParams = new URLSearchParams(searchParams);
    if (newOutlet) {
      newParams.set("outletId", newOutlet.id);
    } else {
      newParams.delete("outletId");
    }
    // Reset pagination when changing outlet
    resetParams.forEach((param) => newParams.delete(param));
    setSearchParams(newParams);
  };

  return (
    <PageContainer
      title="Waiting List Lensa Pre-Order"
      breadcrumbs={[]}
      maxWidth={false}
    >
      <Stack spacing={3}>
        <Grid container spacing={2} sx={{ justifyContent: "flex-end" }}>
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <UserOutletsSelect
              value={outlet}
              onChange={handleOutletChange}
              disableClearable={false}
              allowedRoles={[OutletRoleEnum.RETAIL]}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari nomor referensi"
              {...searchProps}
            />
          </Grid>
        </Grid>
        <DataGrid
          rows={salesOrderLensProducts}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "referenceNumber",
              headerName: "No. SO #",
              flex: 1.5,
              sortable: false,
              renderCell: ({ row }) => row.salesOrder.referenceNumber,
            },
            {
              field: "transactionDate",
              headerName: "Tanggal",
              flex: 1,
              sortable: false,
              renderCell: ({ row }) =>
                formatDate(row.salesOrder.transactionDate),
            },
            {
              field: "seller",
              headerName: "Cabang",
              flex: 1,
              sortable: false,
              valueGetter: (_, { salesOrder: { seller } }) => seller.name,
            },
            {
              field: "customer",
              headerName: "Pelanggan Retail",
              flex: 1,
              sortable: false,
              valueGetter: (_, { salesOrder: { customer } }) => customer.name,
            },
            {
              field: "name",
              headerName: "Lensa",
              flex: 1.5,
              sortable: false,
            },
            {
              field: "variant",
              headerName: "Varian",
              flex: 2,
              sortable: false,
              renderCell: ({ row }) => {
                if (!row.salesOrderLensProductVariants?.length) return "-";

                return (
                  <Stack spacing={0.5}>
                    {row.salesOrderLensProductVariants.map((variant) => {
                      const sideLabel = variant.side === "LEFT" ? "L" : "R";

                      // Get axis from note
                      let axis = "-";
                      try {
                        const parsed = JSON.parse(variant.note || "{}");
                        axis = parsed.axis || "0";
                      } catch {
                        // Ignore JSON parse errors
                      }

                      const value1 = variant.variantAttribute1Value || "-";
                      const value2 = variant.variantAttribute2Value || "-";
                      const value3 = variant.variantAttribute3Value || "0"; // Use "0" if null

                      const prescription = `${value1}/${value2}/${axis}/${value3}`;

                      return (
                        <span key={variant.id}>
                          {sideLabel}: {prescription}
                        </span>
                      );
                    })}
                  </Stack>
                );
              },
            },
            {
              field: "note",
              headerName: "Note",
              flex: 1.5,
              sortable: false,
              renderCell: ({ row }) => row.note || "-",
            },
            ...(hasPermission(PermissionEnum.CREATE_PURCHASE_ORDERS_LAB)
              ? [
                  {
                    field: "actions",
                    type: "actions",
                    headerName: "Aksi",
                    width: 80,
                    getActions: (params) => {
                      const actions = [];
                      actions.push(
                        <IconButton
                          key="create"
                          onClick={() => navigate(`${params.row.id}/create`)}
                        >
                          <ShoppingBagIcon />
                        </IconButton>,
                      );
                      return actions;
                    },
                  },
                ]
              : []),
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
      </Stack>
    </PageContainer>
  );
};
